import { useUpdateTenant } from "api/tenant/service";
import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    CustomInput,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Collapse,
} from "reactstrap";
import EmailSettings from "../settings/EmailSettings";
import WebsiteSettings from "../settings/WebsiteSettings";
import Institution from "../settings/Institution/Institution";
import Flags from "../settings/Flags/Flags";
import TenantLogs from "../logs/TenantLogs";
import ReactTable from "components/ReactTable/ReactTable";
import ReactTable2 from "components/ReactTable/ReactTable2";
import { formatPhoneNumber } from "utils/phoneUtils";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useGetTenant } from "api/tenant/service";
import { useGetStatuses } from "api/inventory-status/service";
import SubHeader from "components/Navbars/SubHeader";
import SADealsTab from "./components/SADealsTab";
import SAChannelsTab from "./components/SAChannelsTab";
import SAAccountsTab from "./components/SAAccountsTab";
import SAInventoryTab from "./components/SAInventoryTab";
import classNames from "classnames";
import { MdOutlineFlag } from "react-icons/md";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";

const WebMaker = React.lazy(() => import("../web-maker/WebMaker"));

const tenantEditLayoutCss = `
  .tenant-edit-page .tenant-edit-form-card-row {
    margin-top: 0;
    margin-bottom: 0;
  }
  .tenant-edit-page .tenant-edit-form-card.card {
    margin-bottom: 4px !important;
  }
  .tenant-edit-page .tenant-edit-form-card > .card-header {
    padding: 8px 12px 4px !important;
  }
  .tenant-edit-page .tenant-edit-form-card > .card-body {
    padding-top: 6px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
  .tenant-edit-page .tenant-edit-form-card .card-body form > .row:first-child {
    margin-top: 0;
  }
  .tenant-edit-page .tenant-edit-form-card .card-body form > .row:first-child .form-group {
    margin-bottom: 0.75rem;
  }
`;

const TenantEdit = ({ activeColor = "secondary", shellThemeVariant = "1", account }) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        isTheme2,
        inputStyle,
        inputClassName,
        theme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        activeColorStyle,
        pageKey: "tenant-edit",
        insetClass: THEME2_ADMIN_INSETS.tenants,
        withSelectStyles: true,
    });

    // Tema rengine göre başlık rengi seçimi
    const headingColorStyle = useMemo(() => {
        const headingColorMap = {
            primary: { color: 'var(--theme-primary)' },
            secondary: { color: 'var(--theme-secondary)' },
            blue: { color: 'var(--theme-blue)' },
            green: { color: 'var(--theme-green)' },
            orange: { color: 'var(--theme-orange)' },
            red: { color: 'var(--theme-red)' }
        };
        return headingColorMap[activeColor] || headingColorMap.secondary;
    }, [activeColor]);

    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const updateTenantMutation = useUpdateTenant();

    // URL'den tab değerini al, yoksa varsayılan olarak 'USERS' kullan
    const currentTab = searchParams.get('tab')?.toUpperCase() || 'USERS';
    const [activeTab, setActiveTab] = useState(currentTab);

    // activeTab değiştiğinde URL'yi güncelle
    useEffect(() => {
        setSearchParams({ tab: activeTab.toLowerCase() }, { replace: true });
    }, [activeTab, setSearchParams]);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(activeTab === "LOGS" ? 50 : 10);
    const [isLightTheme, setIsLightTheme] = useState(false);


    const tenantFetchType =
        activeTab === "INSTITUTION" || activeTab === "FLAGS"
            ? "users"
            : activeTab === "WEBSITE" || activeTab === "WEBSITE_SETTINGS"
              ? "website_settings"
              : activeTab.toLowerCase();

    const { data: tabData, isLoading: isTabLoading, isError } = useGetTenant(id, tenantFetchType, page, pageSize, {});

    console.log("tabData", tabData)
    const { data: statusesData, isLoading: isLoadingStatuses } = useGetStatuses();
    const statusOptions = useMemo(() => {
        const results = statusesData?.results || [];
        return results.map((s) => ({
            label: s.status_name || s.name || s.label,
            value: s.status_key || s.value || s.id
        }));
    }, [statusesData]);

    // Tema kontrolü
    React.useEffect(() => {
        const checkTheme = () => {
            setIsLightTheme(document.body.classList.contains('white-content'));
        };
        checkTheme();

        const observer = new MutationObserver(checkTheme);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    const [isFormOpen, setIsFormOpen] = useState(false);

    React.useEffect(() => {
        setPage(1);
        setPageSize(activeTab === 'LOGS' ? 50 : 10);
    }, [activeTab]);

    const [formData, setFormData] = useState({
        email: '',
        alt_email: '',
        tenant_name: '',
        tenant_status: true,
        phone: '',
    });

    const embeddedWebMakerTenant = useMemo(
        () =>
            id
                ? {
                      id,
                      name: tabData?.tenant_name || tabData?.name || "",
                      code: tabData?.code || "",
                  }
                : null,
        [id, tabData?.tenant_name, tabData?.name, tabData?.code]
    );

    const [errors, setErrors] = useState({});

    const usersColumns = React.useMemo(() => [
        { Header: "ID", accessor: row => row?.id || 'N/A' },
        { Header: "Full Name", accessor: row => row.user_detail?.full_name || 'N/A' },
        { Header: "Email", accessor: "email" },
        { Header: "Phone", accessor: row => row.user_detail?.cell_phone || 'N/A' },
        { Header: "Role", accessor: row => row?.user_role || 'User', Cell: ({ value }) => <span className="badge badge-primary">{value}</span> },
        { Header: "Status", accessor: row => row.confirmed ? 'Active' : 'Inactive', Cell: ({ value }) => <span className={`badge ${value === 'Active' ? 'badge-success' : 'badge-danger'}`}>{value}</span> },
        { Header: "Last Login", accessor: row => row.last_login_at ? new Date(row.last_login_at).toLocaleDateString() : 'N/A' },
        { Header: "Created At", accessor: row => row.createdAt ? new Date(row.createdAt).toLocaleDateString() : 'N/A' },
        {
            Header: "Actions",
            id: "actions",
            accessor: () => "actions",
            Cell: ({ row }) => (
                <div className="text-right d-flex justify-content-end gap-2">
                    <Button
                        size="sm"
                        color="link"
                        className="btn-icon btn-link like table-edit-btn"
                        onClick={() => navigate(`/tenant/${id}/user/${row.original?.user_detail?.id}`)}
                    >
                        <i className="tim-icons icon-pencil" />
                    </Button>
                    <Button size="sm" color="link" className="btn-icon btn-link like table-delete-btn">
                        <i className="tim-icons icon-simple-remove" />
                    </Button>
                </div>
            )
        }
    ], [navigate, id]);


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = type === 'checkbox' ? checked : value;

        if (['work_phone', 'home_phone', 'cell_phone', 'fax', 'phone'].includes(name)) {
            processedValue = formatPhoneNumber(value);
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const UsersTable = isTheme2 ? ReactTable2 : ReactTable;

    const tenantTabNavClass = (isActive) =>
        `${isActive ? "active font-weight-bold" : "text-muted"} d-inline-flex align-items-center`;

    const tenantTabIconStyle = { lineHeight: 1 };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const submitData = {
            name: formData.tenant_name,
            email: formData.email,
            alt_email: formData.alt_email,
            phone: formData.phone,
            tenant_status: formData.tenant_status ? 1 : 0
        };

        try {
            await updateTenantMutation.mutateAsync({ id, data: submitData });
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleCancel = () => {
        navigate('/tenant');
    };

    React.useEffect(() => {
        if (tabData) {
            const tenant = tabData;
            setFormData(prev => ({
                ...prev,
                email: tenant.email || '',
                alt_email: tenant.alt_email || '',
                tenant_name: tenant.name || '',
                tenant_status: tenant.tenant_status === 1,
                phone: tenant.phone || '',
            }));
        }
    }, [tabData]);

    if (isTabLoading && !tabData) {
        return (
            <div className={contentClassName}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Spinner color="primary" />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className={contentClassName}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <h4>Error loading tenant data</h4>
                    <p>Please try again later.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={contentClassName}>
            <Theme2FormFieldsStyle css={theme2FieldsCss} />
            <style>{tenantEditLayoutCss}</style>
            <SubHeader
                compact
                activeColor={activeColor}
                back={`/tenant`}
                title={(tabData?.name || id)}
                status={tabData?.tenant_status == 1 ? "Active" : "Inactive"}
                theme2ScrollGapFill={isTheme2}
                isLightTheme={isLightTheme}
            />
            <Row className="tenant-edit-form-card-row">
                <Col md="12">
                    <Card className="tenant-edit-form-card">
                        <CardHeader
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <CardTitle tag="h4" className="mb-0" style={{ ...headingColorStyle, marginTop: '0px' }}>
                                Edit Tenant
                            </CardTitle>
                            <i
                                className={`tim-icons icon-minimal-${isFormOpen ? 'up' : 'down'}`}
                                style={{ fontSize: '1rem' }}
                            />
                        </CardHeader>
                        <Collapse isOpen={isFormOpen}>
                            <CardBody>
                                <Form onSubmit={handleSubmit} className={formFieldsClassName}>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="email">Email *</Label>
                                                <Input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className={inputClassName(!!errors.email)}
                                                    style={inputStyle}
                                                    invalid={!!errors.email}
                                                />
                                                {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="alt_email">Alternative Email</Label>
                                                <Input
                                                    id="alt_email"
                                                    name="alt_email"
                                                    type="email"
                                                    value={formData.alt_email}
                                                    onChange={handleInputChange}
                                                    className={inputClassName(false)}
                                                    style={inputStyle}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="tenant_name">Tenant Name</Label>
                                                <Input
                                                    id="tenant_name"
                                                    name="tenant_name"
                                                    type="text"
                                                    value={formData.tenant_name}
                                                    onChange={handleInputChange}
                                                    className={inputClassName(false)}
                                                    style={inputStyle}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="phone">Phone</Label>
                                                <Input
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className={inputClassName(false)}
                                                    style={inputStyle}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <CustomInput
                                                    type="switch"
                                                    id="tenant_status"
                                                    name="tenant_status"
                                                    label="Active Status"
                                                    checked={formData.tenant_status}
                                                    onChange={handleInputChange}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="12">
                                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                                <Button
                                                    color="secondary"
                                                    onClick={handleCancel}
                                                    disabled={updateTenantMutation.isPending}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    style={{ ...activeColorStyle, color: 'white' }}
                                                    disabled={updateTenantMutation.isPending}
                                                >
                                                    {updateTenantMutation.isPending ? (
                                                        <>
                                                            <Spinner size="sm" className="mr-2" />
                                                            Updating...
                                                        </>
                                                    ) : (
                                                        'Update Tenant'
                                                    )}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Collapse>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-1 mb-1">
                <Col lg="2" md="6" sm="6">
                    <Card style={{ borderTop: '3px solid #1d8cf8',  height: '100%' }}>
                        <CardBody className="py-3 px-4">
                            <div className="d-flex justify-content-between align-items-center h-100">
                                <div>
                                    <h6 className="mb-1 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '1px', color: '#1d8cf8' }}>USERS</h6>
                                    <h3 className="mb-0 font-weight-bold" style={{ fontSize: '24px' }}>{tabData?.usersCount || 0}</h3>
                                </div>
                                <div>
                                    <i className="tim-icons icon-single-02 text-muted" style={{ fontSize: '24px', opacity: 0.5 }} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="6" sm="6">
                    <Card style={{ borderTop: '3px solid #00f2c3',  height: '100%' }}>
                        <CardBody className="py-3 px-4">
                            <div className="d-flex justify-content-between align-items-center h-100">
                                <div>
                                    <h6 className="mb-1 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '1px', color: '#00f2c3' }}>INVENTORY</h6>
                                    <h3 className="mb-0 font-weight-bold" style={{ fontSize: '24px' }}>{tabData?.inventoriesCount || 0}</h3>
                                </div>
                                <div>
                                    <i className="tim-icons icon-bus-front-12 text-muted" style={{ fontSize: '24px', opacity: 0.5 }} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="6" sm="6">
                    <Card style={{ borderTop: '3px solid #6c757d',  height: '100%' }}>
                        <CardBody className="py-3 px-4">
                            <div className="d-flex justify-content-between align-items-center h-100">
                                <div>
                                    <h6 className="mb-1 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '1px', color: '#6c757d' }}>DEALS</h6>
                                    <h3 className="mb-0 font-weight-bold" style={{ fontSize: '24px' }}>{tabData?.dealsCount || 0}</h3>
                                </div>
                                <div>
                                    <i className="tim-icons icon-paper text-muted" style={{ fontSize: '24px', opacity: 0.5 }} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="6" sm="6">
                    <Card style={{ borderTop: '3px solid #e14eca',  height: '100%' }}>
                        <CardBody className="py-3 px-4">
                            <div className="d-flex justify-content-between align-items-center h-100">
                                <div>
                                    <h6 className="mb-1 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '1px', color: '#e14eca' }}>ACCOUNTS</h6>
                                    <h3 className="mb-0 font-weight-bold" style={{ fontSize: '24px' }}>{tabData?.calculusCount || 0}</h3>
                                </div>
                                <div>
                                    <i className="tim-icons icon-money-coins text-muted" style={{ fontSize: '24px', opacity: 0.5 }} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg="2" md="6" sm="6">
                    <Card style={{ borderTop: '3px solid #fd5d93',  height: '100%' }}>
                        <CardBody className="py-3 px-4">
                            <div className="d-flex justify-content-between align-items-center h-100">
                                <div>
                                    <h6 className="mb-1 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '1px', color: '#fd5d93' }}>CHANNELS</h6>
                                    <h3 className="mb-0 font-weight-bold" style={{ fontSize: '24px' }}>{tabData?.channelsCount || 0}</h3>
                                </div>
                                <div>
                                    <i className="tim-icons icon-world text-muted" style={{ fontSize: '24px', opacity: 0.5 }} />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-1">
                <Col md="12">
                    <div className="custom-tabs">
                        <div className="d-flex align-items-center justify-content-between mb-0">
                            <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'USERS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'USERS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'USERS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('USERS');
                                        }}
                                    >
                                        <i className="tim-icons icon-single-02 mr-2" style={tenantTabIconStyle} />
                                        Users
                                        {activeTab === 'USERS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'INVENTORY')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'INVENTORY' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'INVENTORY' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('INVENTORY');
                                        }}
                                    >
                                        <i className="tim-icons icon-bus-front-12 mr-2" style={tenantTabIconStyle} />
                                        Inventory
                                        {activeTab === 'INVENTORY' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'DEALS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'DEALS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'DEALS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('DEALS');
                                        }}
                                    >
                                        <i className="tim-icons icon-paper mr-2" style={tenantTabIconStyle} />
                                        Deals
                                        {activeTab === 'DEALS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'ACCOUNTS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'ACCOUNTS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'ACCOUNTS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('ACCOUNTS');
                                        }}
                                    >
                                        <i className="tim-icons icon-money-coins mr-2" style={tenantTabIconStyle} />
                                        Accounts
                                        {activeTab === 'ACCOUNTS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>

                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'CHANNELS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'CHANNELS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'CHANNELS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('CHANNELS');
                                        }}
                                    >
                                        <i className="tim-icons icon-world mr-2" style={tenantTabIconStyle} />
                                        Channels
                                        {activeTab === 'CHANNELS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>

                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'INSTITUTION')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'INSTITUTION' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'INSTITUTION' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('INSTITUTION');
                                        }}
                                    >
                                        <i className="tim-icons icon-bank mr-2" style={tenantTabIconStyle} />
                                        Institution
                                        {activeTab === 'INSTITUTION' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'FLAGS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'FLAGS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'FLAGS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('FLAGS');
                                        }}
                                    >
                                        <MdOutlineFlag className="mr-2" size={18} style={tenantTabIconStyle} />
                                        Flags
                                        {activeTab === 'FLAGS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'LOGS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'LOGS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'LOGS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('LOGS');
                                        }}
                                    >
                                        <i className="tim-icons icon-paper mr-2" style={tenantTabIconStyle} />
                                        Logs
                                        {activeTab === 'LOGS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'EMAIL_SETTINGS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'EMAIL_SETTINGS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'EMAIL_SETTINGS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('EMAIL_SETTINGS');
                                        }}
                                    >
                                        <i className="tim-icons icon-email-85 mr-2" style={tenantTabIconStyle} />
                                        Email Settings
                                        {activeTab === 'EMAIL_SETTINGS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'WEBSITE')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'WEBSITE' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'WEBSITE' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('WEBSITE');
                                        }}
                                    >
                                        <i className="tim-icons icon-laptop mr-2" style={tenantTabIconStyle} />
                                        Website
                                        {activeTab === 'WEBSITE' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={tenantTabNavClass(activeTab === 'WEBSITE_SETTINGS')}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            position: "relative",
                                            cursor: 'pointer',
                                            color: activeTab === 'WEBSITE_SETTINGS' ? activeColorStyle.borderColor : undefined,
                                            fontWeight: activeTab === 'WEBSITE_SETTINGS' ? 700 : 500
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setActiveTab('WEBSITE_SETTINGS');
                                        }}
                                    >
                                        <i className="tim-icons icon-world mr-2" style={tenantTabIconStyle} />
                                        Web Site Settings
                                        {activeTab === 'WEBSITE_SETTINGS' && (
                                            <div style={{
                                                height: 3,
                                                background: activeColorStyle.borderColor,
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: -8
                                            }} />
                                        )}
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>

                        <TabContent activeTab={activeTab} className="mt-0">
                            <TabPane tabId="USERS">
                                {activeTab === 'USERS' && (
                                    <Card>
                                        <CardBody>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div>
                                                    <h5 className="mb-0">Company Users</h5>
                                                    {!isTheme2 && (
                                                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>Total Users: {tabData?.users?.pagination?.total || 0}</span>
                                                    )}
                                                </div>
                                                <Button
                                                    size="sm"
                                                    style={{ ...activeColorStyle, color: 'white' }}
                                                    onClick={() => navigate(`/tenant/${id}/add-user`)}
                                                >
                                                    + Add User
                                                </Button>
                                            </div>
                                            <div
                                                className={isTheme2 ? "inventory-table-wrap" : undefined}
                                                style={isTheme2 ? { fontSize: "13px", minWidth: 0, maxWidth: "100%" } : undefined}
                                            >
                                                <UsersTable
                                                    columns={usersColumns}
                                                    data={tabData?.users?.results || []}
                                                    activeColor={activeColor}
                                                    disableViewportClamp={isTheme2}
                                                    manualPagination={true}
                                                    pageCount={tabData?.users?.pagination?.pageCount || 1}
                                                    pageIndex={(tabData?.users?.pagination?.page || 1) - 1}
                                                    totalItems={tabData?.users?.pagination?.total}
                                                    pageSize={pageSize}
                                                    loading={isTabLoading}
                                                    onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                                                    onPageSizeChange={setPageSize}
                                                    onRowClick={(row) => {
                                                        if (row?.id) navigate(`/tenant/${id}/user/${row.id}`);
                                                    }}
                                                    className={classNames("-striped -highlight", {
                                                        "sticky-stock-table": isTheme2,
                                                    })}
                                                />
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                            </TabPane>
                            <TabPane tabId="INVENTORY">
                                <Card className="mt-0">
                                    <CardBody>
                                        {activeTab === 'INVENTORY' && (
                                            <SAInventoryTab
                                                tenantId={id}
                                                activeColor={activeColor}
                                                isTheme2={isTheme2}
                                            />
                                        )}
                                    </CardBody>
                                </Card>
                            </TabPane>
                            <TabPane tabId="CHANNELS">
                                {activeTab === 'CHANNELS' && <SAChannelsTab tenantId={id} activeColor={activeColor} isTheme2={isTheme2} />}
                            </TabPane>
                            <TabPane tabId="INSTITUTION">
                            <Card className="mt-0">
                            <CardBody>
                                {activeTab === 'INSTITUTION' && (
                                    <Institution activeColor={activeColor} account={tabData} embedded shellThemeVariant={shellThemeVariant} />
                                )}
                                </CardBody>
                                </Card>
                            </TabPane>
                            <TabPane tabId="FLAGS">
                                {activeTab === 'FLAGS' && (
                                    <Flags activeColor={activeColor} account={tabData} embedded shellThemeVariant={shellThemeVariant} />
                                )}
                            </TabPane>
                            <TabPane tabId="EMAIL_SETTINGS">
                                {activeTab === 'EMAIL_SETTINGS' && (
                                    <EmailSettings activeColor={activeColor} account={tabData} shellThemeVariant={shellThemeVariant} />
                                )}
                            </TabPane>
                            <TabPane tabId="WEBSITE_SETTINGS">
                                {activeTab === 'WEBSITE_SETTINGS' && (
                                    <WebsiteSettings activeColor={activeColor} account={tabData} tenantId={id} shellThemeVariant={shellThemeVariant} />
                                )}
                            </TabPane>
                            <TabPane tabId="WEBSITE">
                                {activeTab === 'WEBSITE' && (
                                    <React.Suspense
                                        fallback={
                                            <div role="status" aria-live="polite">
                                                {t("webMaker.loadingEditor", "Loading Web Maker editor…")}
                                            </div>
                                        }
                                    >
                                        <WebMaker
                                            embedded
                                            embeddedTenant={embeddedWebMakerTenant}
                                            activeColor={activeColor}
                                            account={account}
                                            shellThemeVariant={shellThemeVariant}
                                        />
                                    </React.Suspense>
                                )}
                            </TabPane>
                            <TabPane tabId="DEALS">
                                <Card>
                                    <CardBody>
                                        {activeTab === 'DEALS' && <SADealsTab tenantId={id} activeColor={activeColor} isTheme2={isTheme2} />}
                                    </CardBody>
                                </Card>
                            </TabPane>
                            <TabPane tabId="ACCOUNTS">
                                <Card>
                                    <CardBody>
                                        {activeTab === 'ACCOUNTS' && <SAAccountsTab tenantId={id} activeColor={activeColor} isTheme2={isTheme2} />}
                                    </CardBody>
                                </Card>
                            </TabPane>
                            <TabPane tabId="LOGS">
                                {activeTab === 'LOGS' && (
                                    <TenantLogs
                                        activeColor={activeColor}
                                        tenantId={id}
                                        logsData={tabData?.logs}
                                        onPageChange={(p) => setPage(p)}
                                        onPageSizeChange={(s) => setPageSize(s)}
                                        isTheme2={isTheme2}
                                    />
                                )}
                            </TabPane>
                        </TabContent>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default TenantEdit;
